import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Avatar, Form, Input, Modal, Space, Typography} from 'antd';
import {LinkOutlined, UserOutlined} from '@ant-design/icons';
import userApi from 'api/userApi';

const { Text } = Typography;

// http(s) URL만 허용
const isHttpUrl = (v) => {
  if (!v) return true;
  try { const u = new URL(v); return u.protocol === 'http:' || u.protocol === 'https:'; } catch { return false; }
};

// 이미지 로드 확인용 (성공/실패 콜백)
const ImageProbe = ({ url, onResult }) => {
  const cbRef = useRef(onResult);
  cbRef.current = onResult;
  useEffect(() => {
    if (!url) { cbRef.current?.({ ok:false, err:'URL이 비어있습니다.' }); return; }
    let canceled = false;
    const img = new Image();
    img.onload = () => { if (!canceled) cbRef.current?.({ ok:true }); };
    img.onerror = () => { if (!canceled) cbRef.current?.({ ok:false, err:'이미지를 불러올 수 없습니다.' }); };
    img.src = url;
    return () => { canceled = true; };
  }, [url]);
  return null;
};

function UserProfileEditModal({ open, user, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [previewUrl, setPreviewUrl] = useState('');
  const [imgOk, setImgOk] = useState(false);
  const [imgMsg, setImgMsg] = useState('');
  const watchedProfileUrl = Form.useWatch('profileUrl', form);

  // 초기값 세팅/리셋
  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        userNm: user.userNm,
        profileUrl: user.profileUrl,
        eml: user.eml,
        pswd: ''
      });
      setPreviewUrl(user.profileUrl || '');
      setImgOk(!!user.profileUrl);
      setImgMsg('');
    } else if (!open) {
      form.resetFields();
      setPreviewUrl('');
      setImgOk(false);
      setImgMsg('');
    }
  }, [open, user, form]);

  // URL 변경 시 미리보기/검증
  useEffect(() => {
    const v = watchedProfileUrl || '';
    setPreviewUrl(v.trim());
    if (!v) { setImgOk(false); setImgMsg(''); return; }
    if (!isHttpUrl(v)) { setImgOk(false); setImgMsg('http(s) 프로토콜의 올바른 URL을 입력하세요.'); return; }
    setImgMsg('');
  }, [watchedProfileUrl]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        userNm: values.userNm,
        profileUrl: values.profileUrl,
        eml: values.eml,
        pswd: values.pswd || undefined,
      };
      const res = await userApi.updateUser(user.userId, payload);
      const data = res?.data?.data || null;
      onSuccess && onSuccess(data || payload);
    } catch (e) {
      if (e?.errorFields) return; // validation 오류
    }
  };

  const profileUrlRules = useMemo(() => ([
    { validator: async(_, v) => {
        if (!v) return Promise.resolve();
        if (!isHttpUrl(v)) return Promise.reject(new Error('http(s) 프로토콜의 올바른 URL을 입력하세요.'));
        return Promise.resolve();
      } }
  ]), []);

  return (
    <Modal
      open={open}
      title="프로필 수정"
      okText="저장"
      cancelText="취소"
      onOk={handleOk}
      onCancel={onCancel}
      okButtonProps={{ disabled: !user?.userId }}
    >
      <Space align="start" size={16} style={{ width:'100%' }}>
        <div style={{ textAlign:'center' }}>
          <Avatar size={88} src={imgOk ? previewUrl : undefined} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff', border: '4px solid #1890ff' }} />
          <div style={{ marginTop:8 }}>
            <Text type="secondary">미리보기</Text>
          </div>
        </div>
        <div style={{ flex:1 }}>
          <Form form={form} layout="vertical">
            <Form.Item label="로그인ID">
              <Input value={user?.lgnUserNm} disabled />
            </Form.Item>
            <Form.Item name="userNm" label="사용자명" rules={[{ required: true, message: '사용자명을 입력하세요.' }]}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="profileUrl" label="프로필URL" rules={profileUrlRules}>
              <Input prefix={<LinkOutlined />} maxLength={500} placeholder="https://example.com/profile.png" />
            </Form.Item>
            {previewUrl && (
              <div style={{ marginTop: -8, marginBottom: 8 }}>
                {!imgOk && imgMsg && (<Alert type="warning" showIcon message={imgMsg} />)}
                {imgOk && (<Alert type="success" showIcon message="이미지를 불러왔습니다." />)}
              </div>
            )}
            <Form.Item name="eml" label="이메일" rules={[{ required: true, message: '이메일을 입력하세요.' }, { type: 'email', message: '유효한 이메일' }]}>
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="pswd" label="비밀번호(변경 시 입력)">
              <Input.Password maxLength={100} />
            </Form.Item>
          </Form>
        </div>
      </Space>
      {previewUrl && isHttpUrl(previewUrl) && (
        <ImageProbe url={previewUrl} onResult={({ ok, err }) => { setImgOk(!!ok); setImgMsg(err || ''); }} />
      )}
    </Modal>
  );
}

export default UserProfileEditModal;
